import styled, { keyframes } from 'styled-components';
import { device, mtrStyle } from 'src/styles/global';
import theme from 'src/styles/theme';

export interface Props {
  hasComparisonOn: boolean;
}

interface DashboardBoxProps {
  minHeight?: string;
}

interface DashboardWrapperProps {
  $isTrackingReport: boolean; // Caster tracking report
}

export const DashboardWrapper = styled.div<DashboardWrapperProps>`
  position: relative;
  min-height: calc(100vh - 62px);
  margin-bottom: ${({ $isTrackingReport }) =>
    $isTrackingReport ? '48px' : '0'};
`;

export const DashboardContainer = styled.div<DashboardWrapperProps>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  margin: auto;
  padding: 40px
    ${({ $isTrackingReport }) => ($isTrackingReport ? '4px' : '24px')} 0px;
  max-width: ${({ $isTrackingReport }) =>
    $isTrackingReport ? '1600px' : '1334px'}; /* Dashboard max-width */

  @media ${device.mobile} {
    padding: 0px
      ${({ $isTrackingReport }) => ($isTrackingReport ? '4px' : '16px')} 0px;
  }

  @media ${device.tablet} {
    padding: 0px
      ${({ $isTrackingReport }) => ($isTrackingReport ? '4px' : '16px')} 0px;
  }

  @media ${device.ipad} {
    padding: 8px
      ${({ $isTrackingReport }) => ($isTrackingReport ? '4px' : '16px')} 0px;
  }

  @media ${device.laptop} {
    padding: 8px
      ${({ $isTrackingReport }) => ($isTrackingReport ? '4px' : '24px')} 0px;
  }

  @media ${device.desktop} {
    padding: 8px
      ${({ $isTrackingReport }) => ($isTrackingReport ? '4px' : '24px')} 0px;
  }
`;

interface LoadingWrapperProps {
  $isTrackingReport?: boolean;
}

export const LoadingWrapper = styled.div<LoadingWrapperProps>`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: ${theme.colors.neutralC100};
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 40px
    ${({ $isTrackingReport }) => ($isTrackingReport ? '4px' : '24px')} 0px;
  overflow: auto;
  z-index: 997;

  @media ${device.mobile} {
    padding: 0px
      ${({ $isTrackingReport }) => ($isTrackingReport ? '4px' : '16px')} 0px;
  }

  @media ${device.tablet} {
    padding: 0px
      ${({ $isTrackingReport }) => ($isTrackingReport ? '4px' : '16px')} 0px;
  }

  @media ${device.ipad} {
    padding: 8px
      ${({ $isTrackingReport }) => ($isTrackingReport ? '4px' : '16px')} 0px;
  }

  @media ${device.laptop} {
    padding: 8px
      ${({ $isTrackingReport }) => ($isTrackingReport ? '4px' : '24px')} 0px;
  }

  @media ${device.desktop} {
    padding: 8px
      ${({ $isTrackingReport }) => ($isTrackingReport ? '4px' : '24px')} 0px;
  }
`;

const skeletonShimmer = keyframes`
  0% {
    background-position: -400px 0;
  }
  100% {
    background-position: 400px 0;
  }
`;

interface LoadingSkeletonProps {
  $isTrackingReport: boolean; // Caster tracking report
}

export const LoadingSkeleton = styled.div<LoadingSkeletonProps>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: ${({ $isTrackingReport }) =>
    $isTrackingReport ? '1600px' : '1334px'};
  margin: 0 auto;

  @media ${device.mobile} {
    gap: 16px;
  }
  @media ${device.tablet} {
    gap: 16px;
  }
`;

interface SkeletonRowProps {
  $gap?: string;
  $direction?: 'row' | 'column';
}

export const SkeletonRow = styled.div<SkeletonRowProps>`
  display: flex;
  width: 100%;
  gap: ${({ $gap }) => $gap || '16px'};
  flex-direction: ${({ $direction }) => $direction || 'row'};

  @media ${device.mobile} {
    flex-direction: column;
  }
  @media ${device.tablet} {
    flex-direction: column;
  }
`;

interface SkeletonBlockProps {
  $width?: string;
  $height?: string;
  $radius?: string;
}

export const SkeletonBlock = styled.div<SkeletonBlockProps>`
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || '16px'};
  border-radius: ${({ $radius }) => $radius || '8px'};
  background: linear-gradient(
    90deg,
    ${theme.colors.neutralC90} 25%,
    ${theme.colors.neutralC80} 37%,
    ${theme.colors.neutralC90} 63%
  );
  background-size: 400% 100%;
  animation: ${skeletonShimmer} 1.4s ease infinite;
`;

export const DashboardBox = styled.div<DashboardBoxProps>`
  display: flex;
  flex-direction: column;
  position: inherit;
  padding: 32px;
  border-radius: ${mtrStyle.borderRadius};
  box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.25);
  background-color: ${theme.colors.white0};
  min-height: ${({ minHeight }) => minHeight || '0'};

  @media ${device.mobile} {
    padding: 24px;
  }
  @media ${device.tablet} {
    padding: 24px;
  }
  @media ${device.ipad} {
    padding: 24px;
  }
`;

export const DashboardTitle = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${theme.colors.neutralW20};
  padding-bottom: 8px;

  @media ${device.mobile} {
    font-size: ${mtrStyle.mobileFontSize};
  }
  @media ${device.tablet} {
    font-size: ${mtrStyle.mobileFontSize};
  }
  @media ${device.ipad} {
    font-size: ${mtrStyle.mobileFontSize};
  }
`;

export const DashboardChartDesc = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 16px;

  @media ${device.mobile} {
    flex-direction: column;
  }
`;

export const DashboardExportDataWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 8px;

  @media ${device.mobile} {
    justify-content: flex-start;
  }
  @media ${device.tablet} {
    justify-content: flex-start;
  }
`;

export const DashboardSortButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 50px;
  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  border: none;
  background: none;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${theme.colors.mtr60};
  }
`;

export const SelectedSongWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-bottom: 12px;
  gap: 24px;

  @media ${device.mobile} {
    display: flex;
    flex-direction: column;
    padding-top: 0px;
    padding-bottom: 16px;
    gap: 8px;
  }
  @media ${device.tablet} {
    display: flex;
    flex-direction: column;
    padding-top: 0px;
    padding-bottom: 16px;
    gap: 16px;
  }
  @media ${device.ipad} {
    display: flex;
    flex-direction: column;
    padding-top: 0px;
    padding-bottom: 16px;
    gap: 16px;
  }
`;

export const TimelineWrapper = styled.div`
  display: flex;
  flex-direction: row;
  padding-bottom: 24px;

  @media ${device.mobile} {
    padding-bottom: 16px;
  }
  @media ${device.tablet} {
    padding-bottom: 16px;
  }
`;

export const StationLocationWrapper = styled.div`
  display: flex;
  flex-direction: row;
  padding-bottom: 24px;
  gap: 24px;

  @media ${device.mobile} {
    display: flex;
    flex-direction: column;
    padding-bottom: 16px;
    gap: 16px;
  }
  @media ${device.tablet} {
    display: flex;
    flex-direction: column;
    padding-bottom: 16px;
    gap: 16px;
  }
  @media ${device.ipad} {
    display: flex;
    flex-direction: column;
    padding-bottom: 16px;
    gap: 16px;
  }
`;

export const AirplayWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

export const NoDataAvailableWrapper = styled.div`
  align-self: center;
  padding-top: 64px;
  padding-bottom: 40px;
`;

export const ComparisonPreviousPeriodDesktop = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${theme.colors.neutralW60};
  margin-top: 4px;

  @media ${device.mobile} {
    display: none;
  }
`;

export const ComparisonPreviousPeriodMobile = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${theme.colors.neutralW60};
  margin-top: 4px;

  @media ${device.tablet} {
    display: none;
  }

  @media ${device.ipad} {
    display: none;
  }

  @media ${device.laptop} {
    display: none;
  }

  @media ${device.desktop} {
    display: none;
  }
`;

export const ComparisonTooltipWrapper = styled.div`
  background: ${theme.colors.neutralW50};
  border-radius: 5px;
  padding: 10px;
`;

export const ComparisonTooltipPeriod = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: ${theme.colors.neutralW70};
  padding-bottom: 2px;
`;

export const ComparisonTooltipData = styled.div`
  font-size: 14px;
  line-height: 19px;
  color: ${theme.colors.neutralW90};
`;

export const BannerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: 24px;

  @media ${device.mobile} {
    padding-bottom: 0px;
  }
`;

export const SecondHeaderWrapper = styled.div<{ $isTrackingReport: boolean }>`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: space-between;
  align-items: stretch;
  padding-top: 0px;
  max-width: ${({ $isTrackingReport }) =>
    $isTrackingReport ? '1600px' : '1334px'}; /* Dashboard max-width */
  margin: auto;
  width: 100%;

  @media ${device.mobile} {
    display: flex;
    flex-direction: column;
    padding-top: 90px;
    padding-left: ${({ $isTrackingReport }) =>
      $isTrackingReport ? '2px' : '16px'};
    padding-right: ${({ $isTrackingReport }) =>
      $isTrackingReport ? '2px' : '16px'};
    gap: 16px;
  }

  @media ${device.tablet} {
    display: flex;
    flex-direction: column;
    padding-left: ${({ $isTrackingReport }) =>
      $isTrackingReport ? '2px' : '16px'};
    padding-right: ${({ $isTrackingReport }) =>
      $isTrackingReport ? '2px' : '16px'};
    gap: 16px;
  }

  @media ${device.ipad} {
    display: flex;
    flex-direction: column;
    padding-left: ${({ $isTrackingReport }) =>
      $isTrackingReport ? '2px' : '16px'};
    padding-right: ${({ $isTrackingReport }) =>
      $isTrackingReport ? '2px' : '16px'};
  }

  /* Second header */
  @media ${device.laptop} {
    position: sticky;
    top: ${({ $isTrackingReport }) => ($isTrackingReport ? '0px' : '89px')};
    background-color: ${theme.colors.neutralC100};
    z-index: 99;
    max-width: ${({ $isTrackingReport }) =>
      $isTrackingReport ? '1600px' : '1334px'}; /* Dashboard max-width */
    padding: ${({ $isTrackingReport }) =>
        $isTrackingReport ? '0px 4px' : '2px 24px'}
      8px;
  }

  /* Second header */
  @media ${device.desktop} {
    position: sticky;
    top: ${({ $isTrackingReport }) => ($isTrackingReport ? '0px' : '89px')};
    background-color: ${theme.colors.neutralC100};
    z-index: 99;
    max-width: ${({ $isTrackingReport }) =>
      $isTrackingReport ? '1600px' : '1334px'}; /* Dashboard max-width */
    padding: ${({ $isTrackingReport }) =>
        $isTrackingReport ? '0px 4px' : '2px 24px'}
      8px;
  }
`;

export const SecondRowWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
  gap: 8px;

  @media ${device.mobile} {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  @media ${device.tablet} {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  @media ${device.ipad} {
    display: flex;
    flex-direction: column;
  }
`;

export const AirplayReportHeading = styled.div`
  font-size: 30px;
  font-weight: 700;
  color: ${theme.colors.neutralW20};
  margin-bottom: 24px;

  @media ${device.mobile} {
    margin-bottom: 0px;
  }

  @media ${device.tablet} {
    margin-bottom: 0px;
  }
`;

export const UniversalFilterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 16px;

  @media ${device.mobile} {
    display: flex;
    flex-direction: column-reverse;
    gap: 8px;
  }

  @media ${device.tablet} {
    display: flex;
    flex-direction: column-reverse;
    gap: 8px;
  }

  @media ${device.ipad} {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
  }
`;

export const VerticalLine = styled.div<Props>`
  display: flex;
  height: 36px;
  border-right: 1px solid ${theme.colors.neutralW80};
  align-self: center;
  margin-bottom: ${({ hasComparisonOn }) => (hasComparisonOn ? '24px' : '0px')};

  @media ${device.mobile} {
    height: 0px;
    border-right: hidden;
    margin-bottom: 0px;
  }

  @media ${device.tablet} {
    height: 0px;
    border-right: hidden;
    margin-bottom: 0px;
  }
`;

export const NoInsigthsStyle = styled.div`
  padding-bottom: 12px;

  @media ${device.mobile} {
    padding-bottom: 0px;
  }
`;
